import { serializeError } from './serialize-error.ts'

type LogMetadata = Record<string, string | number | boolean | null>

/**
 * What React knows about where a render crashed. The stack trace of a render crash only holds React internals, so the
 * component stack is the only way to name the component that failed.
 */
type ErrorReportOptions = {
  componentStack?: string | undefined
}

type LoggingTransport = {
  error?(error: Error, options: ErrorReportOptions): void
  event?(event: string, metadata: LogMetadata): void
}

// Very simple rate limiting mechanism to protect against accidental flooding of messages.
let remainingMessages = 10
setInterval(() => {
  remainingMessages = 10
}, 3600_000)

class Logger {
  private transports: Array<LoggingTransport> = []

  /**
   * Reports a problem. This is always logged to the console, and additionally sent to sentry in production and review environments.
   */
  error(error: Error, options: ErrorReportOptions = {}): void {
    this.transports.forEach((transport) => transport.error?.(error, options))

    // for dev
    // The string survives console capture pipelines that keep no message on Firefox, the flat record
    // carries the cause chain, tRPC data and context, and the live object stays explorable in devtools.
    // eslint-disable-next-line no-console -- logger primitive: console is the underlying transport
    console.error(`${error.name}: ${error.message}`, serializeError(error), error)
  }

  /**
   * Adds a transport that will be notified of errors and events.
   */
  addTransport(transport: LoggingTransport): void {
    this.transports.push(transport)
  }

  /**
   * Sends an event to the server for logging purpose.
   * @param event
   * @param metadata
   */
  event(event: string, metadata: LogMetadata = {}): void {
    if (remainingMessages-- <= 0) return

    this.transports.forEach((transport) => transport.event?.(event, metadata))
  }

  info(msg: string, metadata: LogMetadata = {}): void {
    // same logic as on the backend ("logger.info is syntactic sugar")
    this.event('info', { msg, ...metadata })

    // for dev
    // eslint-disable-next-line no-console -- logger primitive: console is the underlying transport
    console.info(msg, metadata)
  }

  warning(msg: string, metadata: LogMetadata = {}): void {
    this.event('warning', { msg, ...metadata })

    // for dev
    // eslint-disable-next-line no-console -- logger primitive: console is the underlying transport
    console.warn(msg, metadata)
  }
}

export const logger = new Logger()
